import { useState } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Overline, FadeIn } from "@/components/site/Shared";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We respond within one business day.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="py-24 sm:py-32 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeIn>
            <Overline className="mb-5">Contact</Overline>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white leading-[0.95]">
              Let's Talk <span className="text-ember">Construction</span>
            </h1>
          </FadeIn>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-16">
          <FadeIn className="lg:col-span-2">
            <div className="space-y-px bg-line border border-line">
              {[
                { icon: MapPin, title: "Head Office", lines: ["Malom, Near Oil Depot,", "Imphal, Manipur 795140"] },
                { icon: Phone, title: "Phone", lines: ["+91 70059 73358"] },
                { icon: Mail, title: "Email", lines: ["ogkinfra@gmail.com"] },
                { icon: Clock, title: "Working Hours", lines: ["Mon–Sat: 9:00 AM – 7:00 PM", "Site visits by appointment"] },
              ].map((c) => (
                <div key={c.title} className="bg-graphite p-7 flex gap-5">
                  <c.icon size={22} className="text-ember shrink-0 mt-1" strokeWidth={1.5} />
                  <div>
                    <h3 className="font-heading text-white uppercase tracking-wide text-sm">{c.title}</h3>
                    {c.lines.map((l) => <p key={l} className="text-silver text-sm mt-1.5">{l}</p>)}
                  </div>
                </div>
              ))}
            </div>
            <div className="border border-line mt-px">
              <iframe
                title="OGK Head Office Map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=93.885%2C24.715%2C93.965%2C24.790&layer=mapnik&marker=24.752%2C93.925"
                className="w-full h-64 grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </FadeIn>

          <FadeIn delay={0.1} className="lg:col-span-3">
            <form onSubmit={submit} className="border border-line bg-graphite p-8 sm:p-12" data-testid="contact-form">
              <h2 className="font-heading text-2xl uppercase tracking-tight text-white mb-8">Send Us A Message</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Full Name *</label>
                  <Input required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name-input" className="rounded-none bg-carbon border-line h-12" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Email *</label>
                  <Input required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="contact-email-input" className="rounded-none bg-carbon border-line h-12" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Phone</label>
                  <Input value={form.phone} onChange={set("phone")} placeholder="+91" data-testid="contact-phone-input" className="rounded-none bg-carbon border-line h-12" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Subject</label>
                  <Input value={form.subject} onChange={set("subject")} placeholder="What is this about?" data-testid="contact-subject-input" className="rounded-none bg-carbon border-line h-12" />
                </div>
              </div>
              <div className="mt-6">
                <label className="text-xs uppercase tracking-[0.15em] text-silver block mb-2">Message *</label>
                <Textarea required value={form.message} onChange={set("message")} rows={6} placeholder="Tell us about your requirement…" data-testid="contact-message-input" className="rounded-none bg-carbon border-line" />
              </div>
              <Button type="submit" disabled={sending} data-testid="contact-submit-btn"
                className="mt-8 rounded-none bg-ember hover:bg-[#e64a19] text-white uppercase tracking-[0.15em] font-bold px-10 h-14 text-sm">
                {sending ? "Sending…" : "Send Message"}
              </Button>
            </form>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
